const STORAGE_KEY = "sfe-collapsible-sections-v1";

type CollapsibleStore = Record<string, boolean>;

function storageKey(scope: string, sectionId: string): string {
  return `${scope}::${sectionId}`;
}

function readStore(): CollapsibleStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as CollapsibleStore;
  } catch {
    return {};
  }
}

function writeStore(store: CollapsibleStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* private mode / quota */
  }
}

/** Whether a foldable section should render open. */
export function isCollapsibleOpen(
  scope: string,
  sectionId: string,
  defaultOpen = true,
): boolean {
  const store = readStore();
  const key = storageKey(scope, sectionId);
  if (key in store) return store[key] === true;
  return defaultOpen;
}

export function setCollapsibleOpen(scope: string, sectionId: string, open: boolean): void {
  const store = readStore();
  store[storageKey(scope, sectionId)] = open;
  writeStore(store);
}

/** Build a collapsed-id set (for templates that omit `open` when collapsed). */
export function loadCollapsedSectionIds(
  scope: string,
  sectionIds: readonly string[],
  defaultOpen: (sectionId: string) => boolean,
): Set<string> {
  const collapsed = new Set<string>();
  for (const sectionId of sectionIds) {
    if (!isCollapsibleOpen(scope, sectionId, defaultOpen(sectionId))) {
      collapsed.add(sectionId);
    }
  }
  return collapsed;
}

/** HTML attribute fragment for `<details>` — `" open"` or `""`. */
export function collapsibleOpenAttr(
  scope: string,
  sectionId: string,
  defaultOpen = true,
): string {
  return isCollapsibleOpen(scope, sectionId, defaultOpen) ? " open" : "";
}

/** Wire `<details data-sfe-collapsible="id">` elements to localStorage. */
export function bindDetailsCollapsiblePersistence(
  root: ParentNode,
  scope: string,
  selector = "details[data-sfe-collapsible]",
): void {
  root.querySelectorAll<HTMLDetailsElement>(selector).forEach((details) => {
    const sectionId = details.dataset.sfeCollapsible?.trim();
    if (!sectionId) return;

    const defaultOpen = details.dataset.sfeCollapsibleDefault !== "closed";
    const open = isCollapsibleOpen(scope, sectionId, defaultOpen);
    if (details.open !== open) details.open = open;

    if (details.dataset.sfeCollapsibleBound === "1") return;
    details.dataset.sfeCollapsibleBound = "1";
    details.addEventListener("toggle", () => {
      setCollapsibleOpen(scope, sectionId, details.open);
    });
  });
}

/** One-time migration from a legacy localStorage key (value "1" = collapsed). */
export function migrateLegacyCollapsedFlag(
  scope: string,
  sectionId: string,
  legacyKey: string,
  defaultOpen = true,
): void {
  const key = storageKey(scope, sectionId);
  if (key in readStore()) return;
  try {
    const legacy = localStorage.getItem(legacyKey);
    if (legacy == null) return;
    setCollapsibleOpen(scope, sectionId, legacy !== "1");
  } catch {
    setCollapsibleOpen(scope, sectionId, defaultOpen);
  }
}
